import type { fabric } from 'fabric';
import { isJsonString } from '@helpers/isJsonString';
import { AssetVersionCommentDto } from '@api/Api';
import { constrainPositionToWindow } from '@pages/MediaViewer/AssetViewer/Canvas/utils/getObjectViewportPosition';

const VIEWPORT_PADDING = 8;
const DOCUMENT_HIGHLIGHT_GAP = 8;

export function commentHasFloatAnchor(
  comment: AssetVersionCommentDto
): boolean {
  if (
    comment.documentHighlight &&
    (comment.documentHighlightRects?.length ?? 0) > 0
  ) {
    return true;
  }
  if (typeof comment.timeCode?.fromSeconds === 'number') {
    return true;
  }
  if (!comment.annotations || !isJsonString(comment.annotations)) {
    return false;
  }
  try {
    const parsed = JSON.parse(comment.annotations);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

function isNavigableAnnotationComment(
  comment: AssetVersionCommentDto
): boolean {
  if (comment.topParentId) return false;
  return commentHasFloatAnchor(comment);
}

export function getNavigableAnnotationComments(
  commentsList: AssetVersionCommentDto[],
  assetType?: string,
  activePageNumber?: number | null
): AssetVersionCommentDto[] {
  let navigable = commentsList.filter(isNavigableAnnotationComment);

  if (assetType === 'video' || assetType === 'audio') {
    return navigable.sort((a, b) => {
      const timeA = a.timeCode?.fromSeconds ?? Number.POSITIVE_INFINITY;
      const timeB = b.timeCode?.fromSeconds ?? Number.POSITIVE_INFINITY;
      return timeA - timeB;
    });
  }

  if (
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet'
  ) {
    const page = activePageNumber ?? 1;
    navigable = navigable.filter((comment) => comment.documentPage === page);
    return navigable.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }

  return navigable.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

type DocumentHighlightRect = {
  left: number;
  top: number;
  height: number;
};

export function getDocumentHighlightViewportPosition(
  canvas: fabric.Canvas,
  rects: DocumentHighlightRect[]
): { left: number; top: number } | null {
  if (!rects.length) return null;
  const canvasEl = canvas.getElement();
  if (!canvasEl) return null;
  const canvasRect = canvasEl.getBoundingClientRect();
  const lastRect = rects[rects.length - 1];
  const left = canvasRect.left + (lastRect.left / 100) * canvasRect.width;
  const top =
    canvasRect.top +
    ((lastRect.top + lastRect.height) / 100) * canvasRect.height +
    DOCUMENT_HIGHLIGHT_GAP;
  return constrainPositionToWindow({ left, top });
}

export function getFloatCanvasAnnotations(
  canvas: fabric.Canvas,
  options?: { requireHasAnnotations?: boolean; hasAnnotations?: boolean }
): string | null {
  const json = canvas.toJSON(['assetAngle', 'userEmail', 'userId']) as {
    objects?: fabric.Object[];
  };
  const objects = (json.objects || []).filter(
    (item) => item.type !== 'highlight'
  );
  if (!objects.length) return null;
  if (options?.requireHasAnnotations && !options.hasAnnotations) return null;
  return JSON.stringify(objects);
}

export function clampFloatCommentPosition(
  pos: { left: number; top: number },
  size: { width: number; height: number }
): { left: number; top: number } {
  const { width, height } = size;
  const maxLeft = window.innerWidth - width - VIEWPORT_PADDING;
  const maxTop = window.innerHeight - height - VIEWPORT_PADDING;
  return {
    left: Math.max(VIEWPORT_PADDING, Math.min(pos.left, maxLeft)),
    top: Math.max(VIEWPORT_PADDING, Math.min(pos.top, maxTop))
  };
}
